import React, { useEffect, useState, useContext, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { SocketContext } from '../context/SocketContext';
import { FocusableSection, FocusableButton, useBackHandler, setFocus } from '../components/Focusable';

const pulse = keyframes`
  0%, 100% { 
    transform: scale(1); 
    opacity: 0.8; 
  }
  50% { 
    transform: scale(1.05); 
    opacity: 1; 
  }
`;

const ring = keyframes`
  0%, 100% { 
    transform: rotate(-15deg); 
  }
  50% { 
    transform: rotate(15deg); 
  }
`;

const Container = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  width: 100dvw;
  height: 100vh;
  height: 100dvh;
  background: linear-gradient(135deg, #1a1d24 0%, #0a0c10 100%);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 3000;
`;

const CallerAvatar = styled.div`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  background: linear-gradient(135deg, #632ce4, #9d50bb);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
  color: #fff;
  font-weight: bold;
  margin-bottom: 2rem;
  animation: ${pulse} 2s ease-in-out infinite;
  box-shadow: 0 0 40px rgba(99, 44, 228, 0.5);
`;

const CallerName = styled.h1`
  color: #fff;
  font-size: 2.5rem;
  margin: 1rem 0;
`;

const CallerEmail = styled.p`
  color: #888;
  font-size: 1.2rem;
  margin: 0 0 1rem;
`;

const CallStatus = styled.p`
  color: #aaa;
  font-size: 1rem;
  margin-bottom: 3rem;
  animation: ${pulse} 1.5s ease-in-out infinite;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 3rem;
  margin-top: 2rem;
`;

const DeclineButton = styled.button`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  background: linear-gradient(135deg, #e74c3c, #c0392b);
  color: #fff;
  animation: ${ring} 0.5s ease-in-out infinite;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(231, 76, 60, 0.5);
    animation: none;
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const AcceptButton = styled.button`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
  background: linear-gradient(135deg, #27ae60, #2ecc71);
  color: #fff;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(39, 174, 96, 0.5);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

const ButtonLabel = styled.span`
  color: #aaa;
  font-size: 0.9rem;
  margin-top: 0.75rem;
  text-align: center;
`;

const ButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const AutoAnswerCountdown = styled.p`
  color: #2ecc71;
  font-size: 1.3rem;
  margin-top: 2rem;
  font-weight: 600;
  animation: ${pulse} 1.5s ease-in-out infinite;
`;

// Délai avant décrochage automatique pour un contact "senior" (cf. Android TV)
const AUTO_ANSWER_DELAY_SECONDS = 5;

const IncomingCall = ({ callerName, callerEmail, roomId, autoAnswer, onAccept, onDecline }) => {
  const [dots, setDots] = useState('');
  const [countdown, setCountdown] = useState(autoAnswer ? AUTO_ANSWER_DELAY_SECONDS : null);
  const { socket } = useContext(SocketContext);
  const audioRef = useRef(null);
  const handleAcceptRef = useRef(null);

  // En mode décrochage auto (contact senior), le bouton retour est absorbé
  // pour que la personne n'ait pas à interagir.
  useBackHandler(autoAnswer ? () => {} : onDecline);

  // Focus sur le bouton Décrocher par défaut (pas de bouton en décrochage auto)
  useEffect(() => {
    if (autoAnswer) return;
    setTimeout(() => {
      setFocus('ACCEPT_CALL_BUTTON');
    }, 100);
  }, [autoAnswer]);

  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => prev.length >= 3 ? '' : prev + '.');
    }, 500);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    let mounted = true;
    const audio = audioRef.current;

    const tryPlay = () => {
      if (!mounted || !audio) return;

      audio.loop = true;
      audio.play().catch(err => {
        // Ignorer AbortError (composant démonté) et NotAllowedError (autoplay bloqué)
        if (mounted && err.name !== 'AbortError' && err.name !== 'NotAllowedError') {
          console.error('Erreur lecture sonnerie:', err);
        }
        // Si NotAllowedError, réessayer au prochain clic utilisateur
        if (err.name === 'NotAllowedError') {
          const retryOnInteraction = () => {
            if (mounted && audio) {
              audio.play().catch(err => console.warn('Ringtone retry failed:', err.message));
            }
            document.removeEventListener('click', retryOnInteraction);
          };
          document.addEventListener('click', retryOnInteraction, { once: true });
        }
      });
    };

    tryPlay();

    return () => {
      mounted = false;
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
    };
  }, []);

  const getInitials = (name) => {
    return name ? name.substring(0, 2).toUpperCase() : '?';
  };

  const handleDecline = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    
    if (socket) {
      socket.emit('call-declined', { 
        roomId,
        targetEmail: callerEmail
      });
    }
    onDecline();
  };

  const handleAccept = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }

    onAccept();
  };

  // Référence à jour pour le décrochage auto (évite les closures périmées)
  handleAcceptRef.current = handleAccept;

  // Décrochage automatique pour les contacts "senior" (cf. Android TV)
  useEffect(() => {
    if (!autoAnswer) return undefined;
    if (countdown <= 0) {
      handleAcceptRef.current();
      return undefined;
    }
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [autoAnswer, countdown]);

  return (
    <FocusableSection focusKey="INCOMING_CALL">
      <Container>
        <audio ref={audioRef} src="/sounds/ringtone.mp3" />

        <CallerAvatar>{getInitials(callerName)}</CallerAvatar>
        <CallerName>{callerName || 'Contact'}</CallerName>
        <CallStatus>Appel entrant{dots}</CallStatus>

        {autoAnswer ? (
          <AutoAnswerCountdown>
            Décrochage automatique dans {countdown}…
          </AutoAnswerCountdown>
        ) : (
          <ButtonContainer>
            <ButtonWrapper>
              <FocusableButton
                as={DeclineButton}
                onPress={handleDecline}
              >
                ✖
              </FocusableButton>
              <ButtonLabel>Refuser</ButtonLabel>
            </ButtonWrapper>

            <ButtonWrapper>
              <FocusableButton
                as={AcceptButton}
                onPress={handleAccept}
                focusKey="ACCEPT_CALL_BUTTON"
              >
                ✓
              </FocusableButton>
              <ButtonLabel>Décrocher</ButtonLabel>
            </ButtonWrapper>
          </ButtonContainer>
        )}
      </Container>
    </FocusableSection>
  );
};

export default IncomingCall;